import {Component, OnInit, OnDestroy} from '@angular/core';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {PondSectionService} from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import * as _ from 'lodash';
import {ActivatedRoute} from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component BatchEntryDashboardComponent
 * @selector app-batch-entry-dashboard
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `BatchEntryDashboardComponent` serves as the central dashboard for managing batch transactions in the Material Management module.
 * It orchestrates different types of batch entries including Feed Logs, Treatments, Multi-Line Purchases, and Water Quality records.
 *
 * ## Key Responsibilities:
 * 1. **Dashboard Navigation**: Manages the display of different sub-components (Feed Log, Treatment, Purchase, Water Quality) based on user selection.
 * 2. **Context Management**: Handles the selection of Business, Farm Site, and Section to provide context for child components.
 * 3. **Data Retrieval**: Fetches necessary master data like Business Entities, Farm Sites, and Pond Sections based on user roles.
 * 4. **Role-Based Access**: Adapts the interface and data availability based on the logged-in user's role (Farmer, Farm Manager, Business Admin, Employee).
 * 5. **State Management**: Maintains the state of selected filters (Business, Site, Section) and passes them to child components.
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Automatically linked to their business ID. Can view all farm sites associated with the farm.
 *   - **Farm Manager**: Automatically linked to their business ID. Can view only farm sites assigned to them.
 * - **Other Categories (e.g., Employee)**:
 *   - Must search for and select a business entity to view its batch entry dashboard.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display.
 * - `LoginUserName`: Used for display.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial farm sites or businesses.
 * 2. **Selection**: User selects Business (if applicable), Farm Site, and Section.
 * 3. **Navigation**: `currentTab` updates flags to show/hide specific child components (Feed Log, Treatment, etc.).
 * 4. **Child Communication**: Passes selected IDs (`business_id`, `farm_site_id`, `section_id`) to child components via Input properties.
 *
 * ## UI State Management:
 * - **Tabs**: Boolean flags (`currentFeedLogPage`, `currentTreatmentPage`, etc.) control visibility of sub-components.
 * - **Loading**: `responseLoader` manages loading indicators.
 * - **Dropdowns**: Manages business selection dropdown for non-farm users.
 */
@Component({
  selector: 'app-batch-entry-dashboard',
  templateUrl: './batch-entry-dashboard.component.html',
  styleUrls: ['./batch-entry-dashboard.component.scss']
})
export class BatchEntryDashboardComponent implements OnInit, OnDestroy {
  /** @type {boolean} Flag to display the Feed Log page. */
  public currentFeedLogPage: boolean;

  /** @type {boolean} Flag to display the Treatment page. */
  public currentTreatmentPage: boolean;

  /** @type {boolean} Flag to display the Multi-Line Purchase page. */
  public currentMultiLinePurchase: boolean;

  /** @type {boolean} Flag to display the Water Quality page. */
  public currentWaterQualityPage: boolean;

  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PondSectionModel>} List of sections in the selected farm site. */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds (unused in current logic). */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag controlling the main loading spinner. */
  public responseLoader: boolean;

  /** @type {string} Selected Farm Site ID. */
  public farm_site_id: string;

  /** @type {string} Selected Business ID. */
  public business_id: string;

  /** @type {any} Selected Section ID. */
  public section_id: any;

  /** @type {boolean} Flag indicating if search has been clicked (unused). */
  public isSearchClicked: boolean;

  /** @type {string} Current component view state ('list', 'add', 'reset'). */
  public currentComponent: string = 'list';

  /** @type {string} Name of the selected business. */
  public businessName: string;

  /** @type {string} Name of the selected location (unused). */
  public locationName: string;

  /** @type {any} Transaction type ID (unused). */
  public transaction_type_id: any;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {boolean} Flag for dropdown visibility. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered options for business dropdown. */
  public filteredOptions: any[] = [];

  /** @type {string} Business search input placeholder/value. */
  public businessSearch: string = 'Enter the business name';

  /**
   * Constructor for BatchEntryDashboardComponent.
   * Initializes services and sets default tab.
   *
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {FeedLogService} feedLogService - Service for feed logs.
   * @param {CustomerPondService} customerPondService - Service for customer ponds.
   * @param {PondSectionService} pondSectionService - Service for pond sections.
   * @param {BusinessEntityService} businessEntityService - Service for business entities.
   * @param {FarmSiteService} farmSitesService - Service for farm sites.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {BatchTransactionsService} batchTransactionService - Service for batch transactions.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(public route: ActivatedRoute, public customerPondService: CustomerPondService, public pondSectionService: PondSectionService, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public sidenavaction: Sidenavaction, private toastr: ToastrService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.currentFeedLogPage = true;
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Default**: Fetches all business entities for selection.
   *
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.appPermission = this.route.snapshot.data['rolepermission'];
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.LoginUserName = localStorage.getItem('LoginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      /* user login details end */
      this.keyword = 'businessName';
      this.responseLoader = true;
      this.isSearchClicked = false; // if data is not available
      this.farm_site_id = '';
      this.business_id = '';
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.farm_site_id = '';
        this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(localStorage.businessID);
        this.responseLoader = true;
        this.setSingleValueForFormLocation();
        this.business_id = localStorage.businessID;
        this.businessName = this.loginUserBusinessName;
        this.currentTab();
      }
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method filterBusinesses
   * @description Filters business entities based on search input.
   * Updates `filteredOptions`.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  public filterBusinesses(event: KeyboardEvent): void {
    this.filteredOptions = [];
    const value = this.businessSearch && this.businessSearch.replace(/[-_]|\s/g, '').toLowerCase();
    if (value && (event.key != 'Backspace' || event.key == 'Backspace')) {
      this.filteredOptions = this.businessEntities.filter(obj => obj.businessName.replace(/[-_]|\s/g, '').toLowerCase().includes(value));
    } else if (!value) {
      this.filteredOptions = this.businessEntities;
    }
  }

  /**
   * @method selectOption
   * @description Handles business selection from dropdown.
   * Updates business ID and triggers farm site fetching.
   * @param {BusinessEntity} business - The selected business entity.
   * @side-effect Updates `business_id`, `businessSearch`, `isDropdownOpen`, calls `getAllFarmsitesBybusinessId`.
   */
  public selectOption(business: BusinessEntity): void {
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.business_id = business.id;
    if (this.business_id) {
      this.getAllFarmsitesBybusinessId();
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.pondSectionService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getAllFarmsitesBybusinessId
   * @async
   * @description Fetches farm sites for the selected business ID (Admin/Employee flow).
   * Resets farm site ID and triggers single value check.
   * @side-effect Updates `farmSites`, `businessName`, `responseLoader`.
   */
  public async getAllFarmsitesBybusinessId() {
    try {
      this.isSearchClicked = false;
      this.farm_site_id = '';
      this.farmSites = [];
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.business_id);
      this.onFarmsiteSelection();
      /*find business name by selected businessid*/
      const selectedBusinessObj = _.filter(this.businessEntities, (o) => {
        return o.id == this.business_id;
      });
      if (selectedBusinessObj.length > 0) {
        this.businessName = selectedBusinessObj[0].businessName;
      }
      /*find business name by selected businessid*/
      this.responseLoader = true;
      this.setSingleValueForFormLocation();
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method setSingleValueForFormLocation
   * @description Auto-selects the farm site if only one exists and triggers section fetching.
   * @side-effect Updates `farm_site_id`.
   */
  public setSingleValueForFormLocation() {
    if (this.farmSites && this.farmSites.length == 1) {
      this.farm_site_id = this.farmSites[0].id; // model-binding key
      this.getSetionsByFarmsiteId(this.farm_site_id);
      this.onFarmsiteSelection();
    }
  }

  /**
   * @method getSetionsByFarmsiteId
   * @async
   * @description Fetches sections associated with a specific farm site ID.
   * Auto-selects the section if only one exists.
   * @param {string} farmsiteid - The ID of the farm site.
   */
  public async getSetionsByFarmsiteId(farmsiteid: string) {
    try {
      this.section_id = undefined;
      this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmsiteid);
      /*find selected farmsite name*/
      const selectedFarmsiteObj = _.filter(this.farmSites, (o) => {
        return o.id == this.farm_site_id;
      });
      if (selectedFarmsiteObj.length > 0) {
        let farmName = selectedFarmsiteObj[0] && selectedFarmsiteObj[0].farmNameAtLocation;
        let village = selectedFarmsiteObj[0] && selectedFarmsiteObj[0].address && selectedFarmsiteObj[0].address.village;
        this.locationName = (farmName && village) ? (farmName + ',' + village) : ((farmName && !village) ? farmName : (!farmName && village) ? village : '');
      }
      /*end find selected farmsite name*/
      this.setSingleValueForSection();
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method setSingleValueForSection
   * @description Auto-selects the section if only one exists and triggers section selection logic.
   * @side-effect Updates `section_id`.
   */
  public setSingleValueForSection() {
    if (this.pondSections && this.pondSections.length == 1) {
      this.section_id = this.pondSections[0].id; // model-binding key
      this.OnSectionSelection();
    }
    // this.getBatchTransactions();
  }

  /**
   * @method getBatchTransactions
   * @async
   * @description Resets the component view to 'list' mode after a delay.
   */
  public async getBatchTransactions() {
    this.currentComponent = 'reset';
    setTimeout(() => {
      this.currentComponent = 'list';
    }, 500);
  }


  /**
   * @method onGotoAddBatch
   * @description Switches the component view to 'add' mode after a delay.
   */
  public onGotoAddBatch() {
    this.currentComponent = 'reset';
    setTimeout(() => {
      this.currentComponent = 'add';
    }, 500);
  }


  /**
   * @method OnSectionSelection
   * @description Handles section selection change.
   * If currently in 'add' mode, re-triggers add mode to refresh child components.
   */
  public OnSectionSelection() {
    if (this.currentComponent == 'add') {
      this.onGotoAddBatch();
    }
  }

  /**
   * @method onFarmsiteSelection
   * @description Handles farm site selection change.
   * If currently in 'add' mode and Multi-Line Purchase is active, re-triggers add mode.
   */
  public onFarmsiteSelection() {
    if (this.currentComponent == 'add' && this.currentMultiLinePurchase) {
      this.onGotoAddBatch();
    }
  }

  /**
   * @method refreshList
   * @description Refreshes the batch transaction list view.
   */
  public refreshList() {
    this.getBatchTransactions();
  }

  /**
   * @method currentTab
   * @description Switches the active tab (Feed Log, Treatment, etc.) and resets view to list mode.
   * @param {string} type - The tab type identifier.
   */
  public currentTab(type: string = 'waterquality') {
    this.currentComponent = 'list';
    this.currentFeedLogPage = (type == 'feedlog');
    this.currentMultiLinePurchase = (type == 'currentmultilinepurchase');
    this.currentTreatmentPage = (type == 'treatment');
    this.currentWaterQualityPage = (type == 'waterquality');
    if (this.currentMultiLinePurchase) {
      this.getBatchTransactions()
    }
  }
}
